import React, { useMemo } from 'react';
import {
  Box, Stack, Typography, Paper, IconButton, Tooltip, Button, TextField, InputAdornment,
  TableRow, TableCell, useTheme, alpha,
} from '@mui/material';
import PersonCell from '../people/PersonCell';
import { photoFilenameOf } from '../../utils/personPhoto';
import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { Refresh, Search, Clear, MoreVert } from '@mui/icons-material';
import type { AdmissionApplication, AdmissionCycle } from '../../types/admissions';
import SearchableSelect from '../common/SearchableSelect';
import { fmtClassName } from '../../utils/pdf/format';
import { grNumberOf } from '../../utils/admissionRecords';

/**
 * Shared layout pieces for the admission-cycle tabs (Applications, Pipeline,
 * Admit cards, Merit, Decisions, Reports, Settings) so every tab reads as one
 * product: the same toolbar rhythm, table header, panel card and rank badge.
 */

// ─── Toolbar ────────────────────────────────────────────────────────────────

interface TabToolbarProps {
  /** Filters on the left (search, class picker…). */
  children?: React.ReactNode;
  /** Quiet one-line summary next to the filters ("3 pending · 1 issued"). */
  summary?: React.ReactNode;
  /** Actions on the right. */
  actions?: React.ReactNode;
  onRefresh?: () => void;
  refreshing?: boolean;
}

export const TabToolbar: React.FC<TabToolbarProps> = ({ children, summary, actions, onRefresh, refreshing }) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    spacing={1.5}
    alignItems={{ xs: 'stretch', md: 'center' }}
    sx={{ px: 2.5, py: 1.75, borderBottom: 1, borderColor: 'divider' }}
  >
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ flex: 1, minWidth: 0 }}>
      {children}
      {summary && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{summary}</Typography>
      )}
    </Stack>
    {(actions || onRefresh) && (
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
        {onRefresh && (
          <Tooltip title="Refresh">
            <span>
              <IconButton size="small" onClick={onRefresh} disabled={refreshing}><Refresh fontSize="small" /></IconButton>
            </span>
          </Tooltip>
        )}
        {actions}
      </Stack>
    )}
  </Stack>
);

// ─── Tables ─────────────────────────────────────────────────────────────────

/** Header cell style for every admissions table. */
export const tableHeadCellSx: SystemStyleObject<Theme> = {
  fontWeight: 600,
  fontSize: '0.78rem',
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  bgcolor: (t) => alpha(t.palette.text.primary, 0.025),
};

/** Body row: whole row opens the applicant; hover tint. */
export const clickableRowSx: SystemStyleObject<Theme> = {
  cursor: 'pointer',
  '&:last-of-type td': { borderBottom: 0 },
};

/** Container for a table inside a tab — hairline card with rounded corners. */
export const TableCard: React.FC<{ children: React.ReactNode; footer?: React.ReactNode }> = ({ children, footer }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
    {children}
    {footer && (
      <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">{footer}</Typography>
      </Box>
    )}
  </Paper>
);

// ─── Panels ─────────────────────────────────────────────────────────────────

interface PanelCardProps {
  title: string;
  subtitle?: React.ReactNode;
  Icon?: React.ElementType;
  /** Accent for the icon tile. Defaults to the primary colour. */
  color?: string;
  /** Right side of the header (a count, an action). */
  action?: React.ReactNode;
  /** Remove body padding (when the body is a table). */
  flush?: boolean;
  children: React.ReactNode;
}

/** Titled card with an icon tile — Reports panels, grouped lists. */
export const PanelCard: React.FC<PanelCardProps> = ({ title, subtitle, Icon, color, action, flush, children }) => {
  const theme = useTheme();
  const accent = color ?? theme.palette.primary.main;
  return (
    // Fills its grid cell, so panels placed side by side end at the same height.
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', height: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2.5, pt: 2, pb: flush ? 2 : 0 }}>
        {Icon && (
          <Box sx={{
            width: 34, height: 34, borderRadius: 2, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(accent, 0.1), color: accent,
          }}>
            <Icon sx={{ fontSize: 18 }} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
          {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{subtitle}</Typography>}
        </Box>
        {action}
      </Stack>
      <Box sx={flush ? { borderTop: 1, borderColor: 'divider' } : { p: 2.5 }}>{children}</Box>
    </Paper>
  );
};

/** Small uppercase label used above groups inside a tab ("Pending · 3"). */
export const GroupLabel: React.FC<{ children: React.ReactNode; count?: number; action?: React.ReactNode }> = ({ children, count, action }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
    <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
      {children}
    </Typography>
    {count != null && (
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ px: 0.8, borderRadius: 5, bgcolor: (t) => alpha(t.palette.text.primary, 0.06) }}>
        {count}
      </Typography>
    )}
    <Box sx={{ flex: 1 }} />
    {action}
  </Stack>
);

// ─── Rank badge ─────────────────────────────────────────────────────────────

/** Merit rank. Inside the seat cap it reads green; outside, neutral. */
export const RankBadge: React.FC<{ rank: number; inside?: boolean }> = ({ rank, inside }) => {
  const theme = useTheme();
  const color = inside ? theme.palette.success.main : theme.palette.text.secondary;
  return (
    <Box sx={{
      minWidth: 28, height: 28, px: 0.75, borderRadius: 2,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.8rem', fontWeight: 700,
      color: inside ? theme.palette.success.dark : 'text.primary',
      bgcolor: alpha(color, inside ? 0.12 : 0.08),
    }}>
      {rank}
    </Box>
  );
};

// ─── Table pieces ───────────────────────────────────────────────────────────
// Filters, applicant cell, seat cut-line and row ⋮ cell — shared by the
// class-scoped tabs (Merit, Decisions) and available to every tab.

/** Right-aligned figures that line up digit-for-digit down a column. */
/** Segmented toggles (answer pickers, Whole / Halves, Main / Junior): the
 *  chosen segment reads at a glance in the primary tint, not a faint grey. */
export const segmentedSx = (t: Theme): SystemStyleObject<Theme> => ({
  flexWrap: 'wrap',
  '& .MuiToggleButton-root': { px: 1.25, py: 0.4, minWidth: 40, textTransform: 'none', fontSize: '0.8rem', fontVariantNumeric: 'tabular-nums' },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
    bgcolor: alpha(t.palette.primary.main, 0.12),
    color: t.palette.primary.main,
    fontWeight: 700,
  },
});

export const numCellSx = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;

/** Client-side applicant match on name, father's name, application number
 *  or (for a returning student) GR number. */
export const applicantMatches = (app: AdmissionApplication, q: string): boolean =>
  [app.name, app.fatherName, app.applicationNumber, grNumberOf(app)].some(v => v?.toLowerCase().includes(q));

/** The cycle's classes as picker options, for the class-scoped tabs. */
export const useCycleClassOptions = (cycle: AdmissionCycle) => useMemo(
  () => (cycle.allowedClassIds || [])
    .filter(c => typeof c === 'object')
    .map(c => c as { _id: string; name: string }),
  [cycle.allowedClassIds],
);

interface ClassSearchFiltersProps {
  classOptions: Array<{ _id: string; name: string }>;
  classId: string;
  onClassChange: (id: string) => void;
  search: string;
  onSearchChange: (q: string) => void;
  searchPlaceholder: string;
}

/** Class picker + applicant search — the left side of the toolbar. */
export const ClassSearchFilters: React.FC<ClassSearchFiltersProps> = ({
  classOptions, classId, onClassChange, search, onSearchChange, searchPlaceholder,
}) => (
  <>
    <SearchableSelect
      label="Class"
      value={classId}
      onChange={onClassChange}
      options={classOptions.map(c => ({ value: c._id, label: fmtClassName(c.name) }))}
      clearable={false}
      minWidth={200}
    />
    <TextField
      size="small"
      placeholder={searchPlaceholder}
      value={search}
      onChange={(e) => onSearchChange(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: search ? (
          <InputAdornment position="end">
            <IconButton size="small" aria-label="Clear search" onClick={() => onSearchChange('')}>
              <Clear fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{ flex: 1, minWidth: 220, maxWidth: 360 }}
    />
  </>
);

/** Name in bold with the father's name muted underneath. */
export const ApplicantCell: React.FC<{ app: AdmissionApplication }> = ({ app }) => (
  <PersonCell name={app.name} photoFilename={photoFilenameOf(app)} secondary={app.fatherName} />
);

/** Row ⋮ — opens the shared ApplicationActionsMenu without opening the row.
 *  `lead` renders a quiet inline action before it (e.g. "Issue"). */
export const RowActionsCell: React.FC<{
  app: AdmissionApplication;
  onMenu: (anchor: HTMLElement) => void;
  lead?: React.ReactNode;
}> = ({ app, onMenu, lead }) => (
  <TableCell align="right" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: 'nowrap' }}>
    {lead}
    <IconButton
      size="small"
      aria-label={`Actions for ${app.name}`}
      onClick={(e) => onMenu(e.currentTarget)}
    >
      <MoreVert fontSize="small" />
    </IconButton>
  </TableCell>
);

/** Full-width dashed divider between the last row inside the seat cap and the first outside it. */
export const SeatCutLineRow: React.FC<{ colSpan: number; seats: number }> = ({ colSpan, seats }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ py: 0.75, borderBottom: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ flex: 1, borderTop: 1, borderStyle: 'dashed', borderColor: 'success.main', opacity: 0.6 }} />
        <Typography variant="caption" fontWeight={600} color="success.dark" sx={{ whiteSpace: 'nowrap', letterSpacing: 0.2 }}>
          Seat cut-line · {seats} seats
        </Typography>
        <Box sx={{ flex: 1, borderTop: 1, borderStyle: 'dashed', borderColor: 'success.main', opacity: 0.6 }} />
      </Stack>
    </TableCell>
  </TableRow>
);

/** Search narrowed the table to nothing. */
export const NoSearchMatchRow: React.FC<{ colSpan: number; search: string; onClear: () => void }> = ({ colSpan, search, onClear }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ py: 5, textAlign: 'center', color: 'text.secondary', borderBottom: 0 }}>
      <Typography variant="body2">No applicant matches "{search}".</Typography>
      <Button size="small" sx={{ mt: 1, textTransform: 'none' }} onClick={onClear}>Clear search</Button>
    </TableCell>
  </TableRow>
);
